import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { stateToHTML } from 'draft-js-export-html';
import { convertFromRaw } from 'draft-js';
import DOMPurify from 'dompurify';
import {
  makeStyles, tokens,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Button, Spinner,
} from '@fluentui/react-components';
import { API_BASE } from '../config.js';
import { iconMap } from '../theme/icons';

const CloseIcon = iconMap.close;

const useStyles = makeStyles({
  surface: { maxWidth: '40rem', width: '100%' },
  content: {
    maxHeight: '60vh', overflowY: 'auto',
    fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.6',
    '& h1': { fontSize: '1.5rem', fontWeight: tokens.fontWeightBold, marginBottom: tokens.spacingVerticalL, color: tokens.colorNeutralForeground1 },
    '& h2': { fontSize: '1.25rem', fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalXXL, marginBottom: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 },
    '& h3': { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXS, color: tokens.colorNeutralForeground1 },
    '& p': { marginBottom: tokens.spacingVerticalS },
    '& p:last-child': { marginBottom: 0 },
    '& ul': { listStyleType: 'disc', marginLeft: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalS },
    '& ol': { listStyleType: 'decimal', marginLeft: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalS },
    '& li': { marginBottom: tokens.spacingVerticalXXS },
    '& strong': { fontWeight: tokens.fontWeightSemibold },
    '& a': { color: tokens.colorBrandForegroundLink, textDecorationLine: 'underline', textUnderlineOffset: '2px' },
  },
  center: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL },
  errorText: { fontSize: tokens.fontSizeBase300, color: tokens.colorStatusDangerForeground1 },
});

// Pakeliui ištrauktas iš Checkout.jsx (pirmo naudotojo) į bendrą komponentą, kai
// Carpenter.jsx tapo antru — rodo /taisykles ir /privatumo-politika turinį (tas
// pats /api/textpages/:slug šaltinis, kaip Text.jsx) tiesiai dialog lange, kad
// vartotojas galėtų peržiūrėti sąlygas neprarasdamas jau užpildytos formos.
// Savarankiškas (ne `s` prop iš kviečiančiojo) — abu puslapiai naudoja TĄ PATĮ
// stilių, nereikia derinti su kviečiančiojo makeStyles.
function LegalTextDialog({ slug, onClose }) {
  const { i18n } = useTranslation();
  const s = useStyles();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lang = i18n.language?.slice(0, 2) || 'lt';

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    const url = lang !== 'lt' ? `${API_BASE}/textpages/${slug}?lang=${lang}` : `${API_BASE}/textpages/${slug}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('Puslapis nerastas'); return r.json(); })
      .then(setPage)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  let safeHtml = '';
  if (page?.content) {
    try {
      const parsed = JSON.parse(page.content);
      safeHtml = parsed?.blocks ? stateToHTML(convertFromRaw(parsed)) : page.content;
    } catch {
      safeHtml = page.content;
    }
    // /api/textpages/:slug turinys prasideda <h1>Puslapio pavadinimas</h1>, kuris
    // dubliuoja DialogTitle žemiau (žr. /admin/bugs "Atsidarius dialog langa title
    // kartojasi su dialog lango title").
    safeHtml = DOMPurify.sanitize(safeHtml).replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '');
  }

  return (
    <Dialog open={!!slug} onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
      <DialogSurface className={s.surface}>
        <DialogBody>
          <DialogTitle action={<Button appearance="subtle" icon={<CloseIcon />} onClick={onClose} aria-label="Uždaryti" />}>
            {page?.title || ''}
          </DialogTitle>
          <DialogContent className={s.content}>
            {loading && <div className={s.center}><Spinner size="medium" /></div>}
            {error && <p className={s.errorText}>{error}</p>}
            {!loading && !error && <div dangerouslySetInnerHTML={{ __html: safeHtml }} />}
          </DialogContent>
          <DialogActions>
            <Button appearance="outline" onClick={onClose}>Uždaryti</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default LegalTextDialog;
